import React, { useState, useEffect } from 'react';
import { getAppleStoreUrl, getGooglePlayUrl } from '../utils/storeLinks';
import '../styles/InstallPrompt.css';

/**
 * AppDownloadBanner - Dismissable smart banner prompting mobile users
 * to download the native app from the appropriate store.
 *
 * - iOS users see "Download on the App Store"
 * - Android users see "Get it on Google Play"
 * - Desktop users do not see the banner
 * - Remembers dismissal for 7 days in localStorage
 * - Hides if already running inside the native app (standalone/webview)
 */
function AppDownloadBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [platform, setPlatform] = useState(null); // 'ios' | 'android'

  useEffect(() => {
    // Don't show inside native app webview or standalone mode
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone
      || document.referrer.includes('android-app://');

    if (isStandalone) return;

    // Check dismissal
    const dismissed = localStorage.getItem('app-banner-dismissed');
    if (dismissed) {
      const daysSince = (Date.now() - parseInt(dismissed, 10)) / (1000 * 60 * 60 * 24);
      if (daysSince < 7) return;
    }

    // Detect platform
    const ua = window.navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isAndroid = /android/.test(ua);

    if (isIOS) {
      setPlatform('ios');
      setShowBanner(true);
    } else if (isAndroid) {
      setPlatform('android');
      setShowBanner(true);
    }
    // Desktop: don't show
  }, []);

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('app-banner-dismissed', Date.now().toString());
  };

  if (!showBanner || !platform) return null;

  const storeUrl = platform === 'ios' ? getAppleStoreUrl() : getGooglePlayUrl();
  const storeName = platform === 'ios' ? 'App Store' : 'Google Play';

  return (
    <div className="app-download-banner">
      <div className="app-download-banner-content">
        <div className="app-download-banner-icon">
          <img src="/apple-touch-icon.png" alt="Begin Strength" />
        </div>

        <div className="app-download-banner-text">
          <strong>Begin Strength</strong>
          <span>Get the full experience</span>
        </div>

        <a
          className="app-download-banner-button"
          href={storeUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {platform === 'ios' ? 'App Store' : storeName}
        </a>

        <button
          className="app-download-banner-dismiss"
          onClick={handleDismiss}
          aria-label="Dismiss"
        >
          &times;
        </button>
      </div>
    </div>
  );
}

export default AppDownloadBanner;
